import { prefersReducedMotion } from '@theme/utilities';

/*
 * <scroll-parallax> — scroll-linked offsets for the photos of a section.
 *
 *   <scroll-parallax style="display: contents">        wraps a section (no box of its own)
 *     ... <div data-parallax>          --parallax-offset ±6%   (full-bleed statement / newsletter)
 *     ... <a data-parallax="soft">     --parallax-offset ±2%   (home photos)
 *     ... <div data-drift>             --parallax-drift ±40     (unitless, the CSS scales it)
 *   </scroll-parallax>
 *
 * Every instance registers its targets with one shared IntersectionObserver and one scroll listener:
 * a single rAF paints only the targets currently in view. Disabled with prefers-reduced-motion.
 */

/** Targets currently in view */
/** @type {Set<HTMLElement>} */
const visible = new Set();

/** Number of targets registered by connected instances */
let registered = 0;
let frame = 0;

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement)) continue;
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        requestPaint();
      })
    : null;

function paint() {
  frame = 0;
  const viewportHeight = window.innerHeight;
  for (const element of visible) {
    const rect = element.getBoundingClientRect();
    const position = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight; // -1 … 1
    if (element.hasAttribute('data-parallax')) {
      const strength = element.dataset.parallax === 'soft' ? -2 : -6;
      element.style.setProperty('--parallax-offset', `${(position * strength).toFixed(2)}%`);
    } else {
      element.style.setProperty('--parallax-drift', (position * -40).toFixed(1));
    }
  }
}

function requestPaint() {
  if (!frame) frame = requestAnimationFrame(paint);
}

/** A custom element that drives the parallax and drift of the photos inside it */
class ScrollParallax extends HTMLElement {
  /** @type {HTMLElement[]} */
  #targets = [];

  connectedCallback() {
    if (prefersReducedMotion()) return;
    this.#targets = Array.from(this.querySelectorAll('[data-parallax], [data-drift]')).filter(
      /** @returns {element is HTMLElement} */
      (element) => element instanceof HTMLElement
    );
    if (!this.#targets.length) return;

    if (!registered) window.addEventListener('scroll', requestPaint, { passive: true });
    registered += this.#targets.length;
    for (const target of this.#targets) {
      if (observer) observer.observe(target);
      else visible.add(target);
    }
    requestPaint();
  }

  disconnectedCallback() {
    if (!this.#targets.length) return;
    for (const target of this.#targets) {
      observer?.unobserve(target);
      visible.delete(target);
    }
    registered -= this.#targets.length;
    this.#targets = [];
    if (!registered) window.removeEventListener('scroll', requestPaint);
  }
}

if (!customElements.get('scroll-parallax')) {
  customElements.define('scroll-parallax', ScrollParallax);
}
